import {
  quatFromAxisAngle,
  quatMultiply,
  rotatedAabbHalfExtents,
} from '../engine/quat'
import { Quat, Vec3 } from '../types'

/**
 * 車両運転の純粋ロジック。rapier / three に依存しない数学モジュール
 * （engine/quat.ts と同じ方針）。
 *
 * シーン JSON にはウェルド/コンストレイント情報が無いため、車両は
 * partIds の部品一式を 1 つの剛体として kinematic に動かす。乗車時点の
 * 各部品の相対配置（pivot からのオフセットと姿勢）をキャプチャし、以後は
 * 「pivot の位置 + 累積ヨー」だけを積分して全部品の姿勢を再構成する。
 *
 * 速度・操舵のパラメータは Roblox 公式 endorsed vehicle スクリプトの値
 * （mphConversion / MaxSpeed / SteerLimit / exp(-3r²) トルク減衰）を踏襲する。
 */

const DEG = Math.PI / 180

/** mph → studs/秒 換算係数（endorsed vehicle スクリプトの mphConversion=0.6263 の逆数）。 */
export const MPH_TO_STUDS_PER_SECOND = 1 / 0.6263
/** 既定の最高速度（attribute 欠落時）。endorsed テンプレートの MaxSpeed=100mph 相当。 */
export const DEFAULT_MAX_SPEED = 100 * MPH_TO_STUDS_PER_SECOND
/** 既定の後退最高速度。ReverseSpeed=35mph 相当。 */
export const DEFAULT_REVERSE_SPEED = 35 * MPH_TO_STUDS_PER_SECOND

/** アクセル加速度（studs/s²）。exp 減衰前の最大値。 */
const ACCELERATION = 60
/** 進行方向と逆のスロットルで掛かるブレーキ減速度（studs/s²）。 */
const BRAKE_DECELERATION = 98
/** ハンドブレーキ減速度（studs/s²）。throttle より優先する。 */
const HANDBRAKE_DECELERATION = 130
/** 無入力時の惰性減速度（studs/s²、転がり抵抗の近似）。 */
const COAST_DECELERATION = 12
/** ホイールベース（studs）。自転車モデルの旋回半径計算に使う。 */
const WHEELBASE = 11.3
/** 前輪の最大切れ角（rad）。 */
const MAX_WHEEL_ANGLE_RAD = 25 * DEG
/** ヨー角速度の上限（rad/s）。低速×大舵角でのスピン的回転を防ぐ。 */
const MAX_YAW_RATE = 1.4
/** 高速時の操舵減衰係数（endorsed の SteerLimit=0.2 を踏襲）。 */
const STEER_LIMIT = 0.2
/** 座席前方向の XZ 成分がこれ未満なら縦向き座席とみなしヨー 0 に倒す。 */
const FORWARD_XZ_EPSILON = 1e-6

/** 既定の降車オフセット（座席ローカル）。運転席は車体左側・ドアは -X。 */
export const DEFAULT_EXIT_OFFSET: Vec3 = { x: -3, y: 0.5, z: 0 }

export interface VehicleDriveParams {
  readonly maxSpeed: number
  readonly reverseSpeed: number
}

export interface VehicleDriveInput {
  /** -1（後退）| 0 | 1（前進）。 */
  readonly throttle: number
  /** -1 | 0 | 1。+1 が左旋回。 */
  readonly steer: number
  readonly handbrake: boolean
}

export interface VehicleMotion {
  /** pivot の現在位置。 */
  readonly position: Vec3
  /** 乗車時からの累積ヨー（rad）。 */
  readonly yawDelta: number
  /** 符号付き速度（studs/s）。前進が正。 */
  readonly speed: number
}

export interface CapturedVehiclePart {
  readonly id: string
  /** pivot からの乗車時オフセット。 */
  readonly localOffset: Vec3
  /** 乗車時の合成姿勢 Qy(rotationY)∘orientation。 */
  readonly pose0: Quat
}

export interface CapturedVehicleGeometry {
  /** 乗車時の部品 AABB 中心。 */
  readonly pivot: Vec3
  /** 乗車時の部品 AABB 半径（衝突スイープ・降車フォールバックに使う）。 */
  readonly halfExtents: Vec3
  /** 座席前方向（座席ローカル -Z を座席姿勢で回した XZ 方位）のヨー。 */
  readonly forwardYaw0: number
  readonly parts: ReadonlyArray<CapturedVehiclePart>
  readonly seatLocalOffset: Vec3
  readonly seatPose0: Quat
}

/**
 * クォータニオンでベクトルを回す（v' = v + 2w(q×v) + 2q×(q×v)）。
 * 行列を組み立てるより少ない演算で済み、純粋関数で完結する。
 */
export const rotateVecByQuat = (v: Vec3, q: Quat): Vec3 => {
  // t = 2 (q.xyz × v)
  const tx = 2 * (q.y * v.z - q.z * v.y)
  const ty = 2 * (q.z * v.x - q.x * v.z)
  const tz = 2 * (q.x * v.y - q.y * v.x)
  return {
    x: v.x + q.w * tx + (q.y * tz - q.z * ty),
    y: v.y + q.w * ty + (q.z * tx - q.x * tz),
    z: v.z + q.w * tz + (q.x * ty - q.y * tx),
  }
}

/** ヨー θ が向く方向（θ=0 は -Z 向き。rotationY = atan2(-dx, -dz) 規約と整合）。 */
const dirFromYaw = (yaw: number): Vec3 => ({
  x: -Math.sin(yaw),
  y: 0,
  z: -Math.cos(yaw),
})

/** Y 軸ヨー回転 R_y(θ) をベクトルへ適用する。 */
const rotateYaw = (v: Vec3, yaw: number): Vec3 => {
  const c = Math.cos(yaw)
  const s = Math.sin(yaw)
  return { x: v.x * c + v.z * s, y: v.y, z: -v.x * s + v.z * c }
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

/** 0 を跨がずに 0 へ向けて減速させる。 */
const decelerateTowardZero = (
  speed: number,
  deceleration: number,
  dt: number,
): number => {
  if (speed > 0) {
    return Math.max(0, speed - deceleration * dt)
  }
  if (speed < 0) {
    return Math.min(0, speed + deceleration * dt)
  }
  return 0
}

/**
 * 乗車時点の車両ジオメトリをキャプチャする。
 *
 * - 座席が lookup で引けなければ null（車両として成立しない）。
 * - partIds のうち引けない id はスキップ（既に削除済み等）。1 件も無ければ null。
 * - AABB は各部品の position ± rotatedAabbHalfExtents(pose0, size) の min/max。
 */
export const captureVehicleGeometry = (
  lookup: (
    id: string,
  ) =>
    | { position: Vec3; rotationY: number; orientation: Quat; size: Vec3 }
    | undefined,
  partIds: ReadonlyArray<string>,
  seatId: string,
): CapturedVehicleGeometry | null => {
  const seat = lookup(seatId)
  if (!seat) {
    return null
  }

  const resolved: Array<{ id: string; position: Vec3; pose0: Quat }> = []
  let minX = Infinity
  let minY = Infinity
  let minZ = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let maxZ = -Infinity
  for (const id of partIds) {
    const part = lookup(id)
    if (!part) {
      continue
    }
    const pose0 = quatMultiply(
      quatFromAxisAngle('y', part.rotationY),
      part.orientation,
    )
    const half = rotatedAabbHalfExtents(pose0, part.size)
    minX = Math.min(minX, part.position.x - half.x)
    minY = Math.min(minY, part.position.y - half.y)
    minZ = Math.min(minZ, part.position.z - half.z)
    maxX = Math.max(maxX, part.position.x + half.x)
    maxY = Math.max(maxY, part.position.y + half.y)
    maxZ = Math.max(maxZ, part.position.z + half.z)
    resolved.push({ id, position: part.position, pose0 })
  }
  if (resolved.length === 0) {
    return null
  }

  const pivot: Vec3 = {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
    z: (minZ + maxZ) / 2,
  }
  const parts: CapturedVehiclePart[] = resolved.map((part) => ({
    id: part.id,
    localOffset: {
      x: part.position.x - pivot.x,
      y: part.position.y - pivot.y,
      z: part.position.z - pivot.z,
    },
    pose0: part.pose0,
  }))

  const seatPose0 = quatMultiply(
    quatFromAxisAngle('y', seat.rotationY),
    seat.orientation,
  )
  // 座席ローカル -Z（Roblox の座面前方）をワールドへ回して XZ 方位を取る。
  // 縦向き座席（XZ 成分がほぼゼロ）は方位が定まらないためヨー 0 に倒す。
  const forward = rotateVecByQuat({ x: 0, y: 0, z: -1 }, seatPose0)
  const forwardYaw0 =
    Math.hypot(forward.x, forward.z) < FORWARD_XZ_EPSILON ?
      0
    : Math.atan2(-forward.x, -forward.z)

  return {
    pivot,
    halfExtents: {
      x: (maxX - minX) / 2,
      y: (maxY - minY) / 2,
      z: (maxZ - minZ) / 2,
    },
    forwardYaw0,
    parts,
    seatLocalOffset: {
      x: seat.position.x - pivot.x,
      y: seat.position.y - pivot.y,
      z: seat.position.z - pivot.z,
    },
    seatPose0,
  }
}

/**
 * 符号付き速度を 1 ステップ進める。
 *
 * - ハンドブレーキは throttle より優先し、0 へ向けて減速（0 を跨がない）。
 * - 進行方向と逆のスロットルはまずブレーキとして働き、0 で止まる
 *   （次ステップ以降に逆方向へ加速し始める）。
 * - 加速は exp(-3·(speed/max)²) で減衰させ最高速へ漸近させる
 *   （endorsed スクリプトの setMotorTorqueDamped を踏襲）。
 */
export const stepVehicleSpeed = (
  speed: number,
  input: VehicleDriveInput,
  dt: number,
  params: VehicleDriveParams,
): number => {
  if (input.handbrake) {
    return decelerateTowardZero(speed, HANDBRAKE_DECELERATION, dt)
  }
  if (input.throttle > 0) {
    if (speed < 0) {
      return decelerateTowardZero(speed, BRAKE_DECELERATION, dt)
    }
    const ratio = speed / params.maxSpeed
    const next = speed + ACCELERATION * Math.exp(-3 * ratio * ratio) * dt
    return Math.min(next, params.maxSpeed)
  }
  if (input.throttle < 0) {
    if (speed > 0) {
      return decelerateTowardZero(speed, BRAKE_DECELERATION, dt)
    }
    const ratio = speed / params.reverseSpeed
    const next = speed - ACCELERATION * Math.exp(-3 * ratio * ratio) * dt
    return Math.max(next, -params.reverseSpeed)
  }
  return decelerateTowardZero(speed, COAST_DECELERATION, dt)
}

/**
 * 操舵による 1 ステップのヨー増分（rad）を返す。
 *
 * 自転車モデル yawRate = (speed / wheelbase) × tan(舵角)。speed が符号付き
 * なので後退時の操舵反転は自動で成立する。高速ほど舵角を絞り（SteerLimit）、
 * さらに yawRate をクランプして低速×大舵角の過回転を防ぐ。
 */
export const stepVehicleYaw = (
  speed: number,
  steer: number,
  dt: number,
  params: VehicleDriveParams,
): number => {
  const effectiveSteer =
    steer / ((STEER_LIMIT * Math.abs(speed)) / params.maxSpeed + 1)
  const yawRate =
    (speed / WHEELBASE) * Math.tan(effectiveSteer * MAX_WHEEL_ANGLE_RAD)
  return clamp(yawRate, -MAX_YAW_RATE, MAX_YAW_RATE) * dt
}

/** 車両の現在の前方向（単位ベクトル、XZ 平面）。 */
export const vehicleForwardDir = (
  geometry: CapturedVehicleGeometry,
  motion: VehicleMotion,
): Vec3 => dirFromYaw(geometry.forwardYaw0 + motion.yawDelta)

/**
 * 全部品のワールド姿勢を再構成する（World.setDynamicTransforms 用。
 * rotationY=0 契約は呼び出し側）。乗車時の相対配置を pivot まわりに
 * yawDelta だけ回すため、部品間の剛体性が保たれる。
 */
export const computePartTransforms = (
  geometry: CapturedVehicleGeometry,
  motion: VehicleMotion,
): ReadonlyArray<{ objectId: string; position: Vec3; orientation: Quat }> => {
  const yawQuat = quatFromAxisAngle('y', motion.yawDelta)
  return geometry.parts.map((part) => {
    const rotated = rotateYaw(part.localOffset, motion.yawDelta)
    return {
      objectId: part.id,
      position: {
        x: motion.position.x + rotated.x,
        y: motion.position.y + rotated.y,
        z: motion.position.z + rotated.z,
      },
      orientation: quatMultiply(yawQuat, part.pose0),
    }
  })
}

/** 座席のワールド位置と「アバター rotationY に使うヨー」。 */
export const seatWorldPose = (
  geometry: CapturedVehicleGeometry,
  motion: VehicleMotion,
): { position: Vec3; yaw: number } => {
  const rotated = rotateYaw(geometry.seatLocalOffset, motion.yawDelta)
  return {
    position: {
      x: motion.position.x + rotated.x,
      y: motion.position.y + rotated.y,
      z: motion.position.z + rotated.z,
    },
    yaw: geometry.forwardYaw0 + motion.yawDelta,
  }
}

/**
 * 降車位置（ワールド）。座席ローカルの exitOffset を座席の現在姿勢
 * Qy(yawDelta)∘seatPose0 で回し、座席ワールド位置へ加算する。
 */
export const exitWorldPosition = (
  geometry: CapturedVehicleGeometry,
  motion: VehicleMotion,
  exitOffset: Vec3 = DEFAULT_EXIT_OFFSET,
): Vec3 => {
  const seatWorldQuat = quatMultiply(
    quatFromAxisAngle('y', motion.yawDelta),
    geometry.seatPose0,
  )
  const offset = rotateVecByQuat(exitOffset, seatWorldQuat)
  const seat = seatWorldPose(geometry, motion)
  return {
    x: seat.position.x + offset.x,
    y: seat.position.y + offset.y,
    z: seat.position.z + offset.z,
  }
}
